import { Button, Stack, Tabs, Title } from '@mantine/core';
import { useForm } from '@mantine/form';
import { useMemo, useState } from 'react';

import { Declaration, DeclarationStatus, ExitProofLogs } from '@e-origin/shared';

import { useSendProofOfExit } from '@hooks/declaration';
import { ProofOfExitFields } from './ProofOfExitFields';
import { ProofOfExitHistory } from './ProofOfExitHistory';
import { createEmptyAazItem, createEmptyCarrier, hasMeaningfulProofOfExitValue, validateProofOfExit } from './ProofOfExitForm.rules';

interface ProofOfExitFormProps {
  declaration: Declaration;
}

const createInitialProofOfExitValues = (): ExitProofLogs => ({
  exitOffice: '',
  exitDate: null,
  transportDocuments: [],
  additionalInformation: [createEmptyAazItem()],
  carrier: createEmptyCarrier(),
});

export const ProofOfExitForm: React.FC<ProofOfExitFormProps> = ({ declaration }) => {
  const exitProofLogs = declaration.customsState?.exitProof?.logs ?? [];
  const sendProofOfExitMutation = useSendProofOfExit();
  const [resetKey, setResetKey] = useState(0);

  const canSend = useMemo(() => declaration?.generalInfo?.status === DeclarationStatus.RELEASED, [declaration]);

  const form = useForm<ExitProofLogs>({
    initialValues: createInitialProofOfExitValues(),
    validate: (values) => validateProofOfExit(values, declaration),
    validateInputOnChange: true,
  });

  const isFormEmpty = useMemo(() => !hasMeaningfulProofOfExitValue(form.values), [form.values]);

  const handleSubmit = async (proofOfExit: ExitProofLogs) => {
    await sendProofOfExitMutation.mutateAsync({ lrn: declaration.messageInfo.LRN, proofOfExit });
    const initialValues = createInitialProofOfExitValues();
    form.setInitialValues(initialValues);
    form.reset();
    setResetKey((currentValue) => currentValue + 1);
  };

  return (
    <Stack align="flex-start" mb="md">
      <Title order={5} fw={600}>
        Proof of exit
      </Title>

      {canSend ? (
        <Tabs defaultValue="form" w="100%">
          <Tabs.List>
            <Tabs.Tab value="form">Form</Tabs.Tab>
            <Tabs.Tab value="history" disabled={exitProofLogs.length === 0}>
              History ({exitProofLogs.length})
            </Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="form" pt="md">
            <form onSubmit={form.onSubmit(handleSubmit)}>
              <Stack align="flex-start">
                <ProofOfExitFields form={form} declaration={declaration} resetKey={resetKey} />

                <Button type="submit" loading={sendProofOfExitMutation.isPending} disabled={isFormEmpty || !form.isValid()} variant="filled" size="lg">
                  Send proof of exit
                </Button>
              </Stack>
            </form>
          </Tabs.Panel>

          <Tabs.Panel value="history" pt="md">
            <ProofOfExitHistory logs={exitProofLogs} />
          </Tabs.Panel>
        </Tabs>
      ) : (
        <ProofOfExitHistory logs={exitProofLogs} />
      )}
    </Stack>
  );
};
